import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { prettyMDY } from '../../../../shared/utils';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { ButtonGroup, ErrorAlert } from '../../generic';
import Button, { BUTTON_TYPE, LoaderButton } from '../../generic/Button';
import InfoPanel from '../../generic/InfoPanel';
import ExpirationOptions from '../modals/IgnoreWithExceptionsModal/ExpirationOptions';
import type { IssueExceptionsForDashboard } from '../../../../shared/issuesV2';

type ExtendExceptionModalProps = {
  exception: IssueExceptionsForDashboard | null;
  isShowing: boolean;
  onHide: () => void;
  onExtendException: (exceptionId: number, expiresAfter: string | null) => Promise<void>;
};

const ExtendExceptionModal: VoidFunctionComponent<ExtendExceptionModalProps> = ({
  exception,
  isShowing,
  onHide,
  onExtendException,
}) => {
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const asyncStatus = useSelector(IssuesV2Store.selectors.getAsyncStatusExtendException);

  if (!exception) {
    return null;
  }

  const isRuleCurrentlyExpireNever = exception?.expiresAfter === null;

  return (
    <Modal show={isShowing} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>{isRuleCurrentlyExpireNever ? 'Set Expiration' : 'Extend'} Ignore Rule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ExpirationOptions
          setExpirationDate={setExpirationDate}
          startDate={exception.isExpired ? undefined : (exception.expiresAfter as string)}
          shouldDisableNeverOption={isRuleCurrentlyExpireNever}
        />
        {!!asyncStatus?.errors?.length && (
          <ErrorAlert status={asyncStatus} />
        )}
        {expirationDate === null ? (
          (!isRuleCurrentlyExpireNever && (
            <InfoPanel>
              Setting the expiration to <span className="bold">Never</span> will remove the expiration entirely.
            </InfoPanel>
          ))
        ) : (
          <InfoPanel>
            This rule will{isRuleCurrentlyExpireNever ? '' : ' be extended to'} expire on <span className="bold">{prettyMDY(expirationDate)}</span>.
          </InfoPanel>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" onClick={onHide} size="medium">
            Cancel
          </Button>
          <LoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size="medium"
            isLoading={asyncStatus.isLoading}
            onClick={async () => {
              await onExtendException(exception.id, expirationDate);
              onHide();
            }}
          >
            {isRuleCurrentlyExpireNever ? 'Set Expiration' : 'Extend'}
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ExtendExceptionModal;
